const TAB_FOCUS_CLASSES = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40';

/**
 * A tab in a row that scrolls sideways (`BrandPresenceTabStrip`, or a switch that scrolls the same way)
 * keeps its label on one line, and its focus ring is drawn inside it, where the row cannot cut it off.
 */
export const BRAND_PRESENCE_TAB_IN_STRIP_CLASSES = 'shrink-0 whitespace-nowrap focus-visible:ring-inset';

/**
 * Where the stroke of the tab in view sits: on the bottom edge, over the rule a tab strip stands on,
 * or just under the label, for a bar with no rule of its own under the tabs.
 */
export type BrandPresenceTabStroke = 'edge' | 'under-label';

const TAB_STROKE_POSITION_CLASSES: Record<BrandPresenceTabStroke, string> = {
  edge: 'after:-bottom-px',
  'under-label': 'after:bottom-2',
};

/**
 * A primary tab: plain text that darkens on hover and, when it is the one in view, carries a short
 * stroke of the brand gradient under it. The stroke grows in from its centre when the tab is chosen.
 */
export function getBrandPresenceTabClassName(active: boolean, className = '', stroke: BrandPresenceTabStroke = 'edge'): string {
  return [
    'relative inline-flex items-center gap-2 rounded-md border-0 bg-transparent text-sm font-semibold no-underline hover:no-underline transition-colors duration-200',
    "after:pointer-events-none after:absolute after:inset-x-3 after:h-0.5 after:rounded-full after:bg-brand-gradient after:content-[''] after:transition-all after:duration-300 after:ease-out",
    TAB_STROKE_POSITION_CLASSES[stroke],
    TAB_FOCUS_CLASSES,
    active ? 'text-slate-900 after:scale-x-100 after:opacity-100' : 'text-slate-500 hover:text-slate-900 after:scale-x-50 after:opacity-0',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * A secondary tab, one segment of a pill-shaped switch: the chosen segment lifts off the track as a
 * white chip, the others stay flat until hovered.
 */
export function getBrandPresenceSubTabClassName(active: boolean, className = ''): string {
  return [
    'inline-flex items-center rounded-lg px-3 py-1.5 text-[0.8125rem] font-semibold no-underline hover:no-underline transition-all duration-200',
    TAB_FOCUS_CLASSES,
    active ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80' : 'text-slate-500 hover:bg-white/60 hover:text-slate-900',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}
